import { z } from 'zod';
import {
  createPortsSchema,
  UdpPortSelectionSchema,
  type Ports,
  type UdpPortSelection,
} from './scan-config.schema';

export { UdpPortSelectionSchema, type UdpPortSelection };

export const ScanIntensityLevelSchema = z.enum(['low', 'medium', 'high']);

export const DnsBruteforceWordlistSizeSchema = z.enum([
  'small',
  'medium',
  'large',
]);

export const StageToggleSchema = z.object({
  enabled: z.boolean(),
});

export const PermutationDiscoveryConfigSchema = StageToggleSchema.extend({
  intensityLevel: ScanIntensityLevelSchema,
});

export const DnsResolverSchema = z
  .string()
  .min(1)
  .refine(
    (value) =>
      /^(\d{1,3}\.){3}\d{1,3}$/.test(value) ||
      /^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/.test(value),
    {
      error: 'Must be an IPv4 address or hostname',
    },
  );

export const SharedDnsConfigSchema = z.object({
  resolvers: z
    .array(DnsResolverSchema)
    .min(1)
    .max(10)
    .describe(
      'DNS resolvers used by dns-resolution, reverse-dns, and bruteforce verify',
    ),
});

export const AliveTcpPortSelectionSchema = createPortsSchema({
  maxCustomPorts: 1024,
});

export const PortScanTcpPortSelectionSchema = createPortsSchema({
  maxCustomPorts: 65535,
});

export const PortScanStrategySchema = z.enum(['CONNECT', 'SYN']);

export const ScanProfileSettingsSchema = z.object({
  passiveDnsLookup: StageToggleSchema,
  permutationDiscovery: PermutationDiscoveryConfigSchema,
  passiveIntel: StageToggleSchema,
  dns: SharedDnsConfigSchema,
  dnsBruteforce: z.object({
    wordlistSize: DnsBruteforceWordlistSizeSchema,
  }),
  aliveHostDetection: z.object({
    enabled: z.boolean().default(true),
    pingScan: z.boolean(),
    tcpPorts: AliveTcpPortSelectionSchema,
  }),
  portScanning: z.object({
    tcpPorts: PortScanTcpPortSelectionSchema,
    scanStrategy: PortScanStrategySchema,
    udpPorts: UdpPortSelectionSchema,
  }),
  serviceDetection: z.object({
    versionIntensity: ScanIntensityLevelSchema,
  }),
});

export type ScanIntensityLevel = z.infer<typeof ScanIntensityLevelSchema>;
export type DnsBruteforceWordlistSize = z.infer<
  typeof DnsBruteforceWordlistSizeSchema
>;
export type StageToggle = z.infer<typeof StageToggleSchema>;
export type PermutationDiscoveryConfig = z.infer<
  typeof PermutationDiscoveryConfigSchema
>;
export type SharedDnsConfig = z.infer<typeof SharedDnsConfigSchema>;
export type AliveTcpPortSelection = z.infer<typeof AliveTcpPortSelectionSchema>;
export type PortScanTcpPortSelection = Ports;
export type PortScanStrategy = z.infer<typeof PortScanStrategySchema>;
export type ScanProfileSettings = z.infer<typeof ScanProfileSettingsSchema>;
